<template>
    <div>
        <b-modal
            ref="dialog"
            title-tag="h2"
            title="Reservation"
            :lazy="true"
            id="boarding-preview-modal"
            modal-class="reservation-preview"
        >
            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Client:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    <router-link
                        v-if="_hasAccess('client.access')"
                        :to="`/client/profile/${record.clientEncodedId}`"
                    >
                        {{ record.clientName }}
                    </router-link>
                    <span v-else>
                        {{ record.clientName }}
                    </span>

                    <span v-if="record.clientMobile">
                        ({{ record.clientMobile }})
                    </span>

                    <input type="hidden" name="client_id" value="123" />
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Guest:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    <router-link
                        v-if="_hasAccess('patient.access')"
                        :to="`/patient/profile/${record.patientEncodedId}`"
                    >
                        {{ record.patientName }}
                    </router-link>
                    <span v-else>
                        {{ record.patientName }}
                    </span>

                    <span>({{ record.patientSpecies }})</span>

                    <input type="hidden" name="client_id" value="123" />
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Checking In:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    {{ record.checkingIn }}
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Checking Out:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    {{ record.checkingOut }} ({{ record.nights }})
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Room(s):</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    {{
                        record.roomName +
                        " (" +
                        _currency +
                        record.roomFee +
                        "/night)"
                    }}
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Provider:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    {{ record.provider }}
                </b-col>
            </b-row>

            <b-row
                class="mb-3"
                v-if="record.documents && record.documents.length > 0"
            >
                <b-col cols="4">
                    <b>Documents:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    <div
                        v-for="doc in record.documents"
                        :key="doc.id"
                        style="margin-bottom: 3px;"
                    >
                        <router-link
                            v-if="doc.type === 'letter'"
                            :to="`/patient/letter/${doc.encoded_id}`"
                        >
                            {{ doc.label }}
                        </router-link>
                        <a v-else :href="`${baseUrlCi}index.php/${doc.ci_url}`">
                            {{ doc.label }}
                        </a>
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3" v-if="record.invoice">
                <b-col cols="4">
                    <b>Invoice:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    <a
                        :href="`/apps/billing/invoice/view/${record.invoice.encoded_id}`"
                    >
                        {{ record.invoice.name }}
                    </a>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="4">
                    <b>Notes:</b>
                </b-col>
                <b-col cols="8" class="pl-2">
                    <p>
                        {{ record.notes }}
                    </p>
                </b-col>
            </b-row>

            <template v-slot:modal-footer v-if="type === 'CHECKIN'">
                <footer-options
                    @undo="undoCheckIn"
                    @cagecard="printCageCard"
                    @split="split"
                    @extend="openModalExtend"
                    @checkout="openCheckOut"
                    @edit="edit"
                    @checkin="checkin"
                    @cancel="openModalCancel"
                    :status="record.status"
                />
            </template>

            <template v-slot:modal-footer v-else-if="type === 'CHECKOUT'">
                <footer-options
                    @undo="undoCheckIn"
                    @cagecard="printCageCard"
                    @split="split"
                    @extend="openModalExtend"
                    @checkout="openCheckOut"
                    @edit="edit"
                    @checkin="checkin"
                    @cancel="openModalCancel"
                    :status="record.status"
                />
            </template>
        </b-modal>

        <modal-reservation
            ref="modalReservation"
            :no-split-option="true"
            @save="$emit('refresh')"
        />

        <modal-check-in
            ref="modalCheckIn"
            @show="modalLoaded"
            @save-success="$emit('refresh')"
        />

        <modal-undo-check-in ref="modalUndo" @success="$emit('refresh')" />

        <modal-print-cage-card ref="modalCageCard" />

        <modal-cancel
            ref="modalCancel"
            @loaded="modalLoaded"
            @success="$emit('refresh')"
        />

        <modal-extend ref="modalExtend" @loaded="modalLoaded" />

        <modal-check-out
            ref="modalCheckout"
            @loaded="modalLoaded"
            @success="$emit('refresh')"
        />
    </div>
</template>

<script>
import FooterOptions from "@/module/schedule/boarding/reservation-preview-footer.vue";
import BoardingService from "@/services/boarding.js";
import ModalReservation from "@/module/schedule/boarding/modal-reservation/main.vue";
import ModalCheckIn from "@/module/schedule/boarding/modal-check-in.vue";
import ModalUndoCheckIn from "@/module/schedule/boarding/modal-undo-checkin.vue";
import ModalPrintCageCard from "@/module/schedule/appointment/cage-card.vue";
import ModalCancel from "@/module/schedule/boarding/modal-cancel.vue";
import ModalExtend from "@/module/schedule/boarding/modal-extend.vue";
import ModalCheckOut from "@/module/schedule/boarding/modal-check-out.vue";

export default {
    components: {
        FooterOptions,
        ModalReservation,
        ModalCheckIn,
        ModalUndoCheckIn,
        ModalPrintCageCard,
        ModalCancel,
        ModalExtend,
        ModalCheckOut
    },
    name: "dashboard-reservation-modal",
    data() {
        return {
            record: {},
            type: null,
            baseUrlCi: process.env.APPS,
            isNewCalendarOn: false
        };
    },
    computed: {
        isLoadingFooter: {
            get() {
                return this.$store.state.modalReservation
                    .isLoadingFooterPreview;
            },
            set(value) {
                this.$store.commit(
                    "modalReservation/setIsLoadingDetails",
                    value
                );
            }
        }
    },
    methods: {
        open({ record, type = "", isNewCalendarOn }) {
            this.record = record;
            this.type = type.toUpperCase(); // CHECKIN, CHECKOUT
            this.isNewCalendarOn = isNewCalendarOn || false;
            this.$refs.dialog.show();
        },
        getCurrentRecord() {
            return this.record;
        },
        closeAllModalAndPopover() {
            // close all popover and modal if any
            this.$root.$emit("bv::hide::popover");
            this.$root.$emit("bv::hide::modal", "boarding-preview-modal");
        },
        async edit() {
            try {
                this.isLoadingFooter = true;
                let res = await BoardingService.getBoardingById(
                    this.record.recordId
                );
                this.modalLoaded();

                if (!res) {
                    throw new Error("Unable to fetch boarding details");
                }

                this.$refs.modalReservation.openEdit(res);
            } catch (error) {
                this.isLoadingFooter = false;
                this._message(error.message, "danger");
            }
        },
        async checkin() {
            this.isLoadingFooter = true;
            this.$refs.modalCheckIn.open(this.record.recordId);
        },

        undoCheckIn() {
            this.closeAllModalAndPopover();
            this.$refs.modalUndo.open(this.record.recordId);
        },
        async printCageCard() {
            this.isLoadingFooter = true;

            let res = await BoardingService.getBoardingById(
                this.record.recordId
            );

            this.modalLoaded();

            if (res) {
                this.$refs.modalCageCard.open({
                    client: res.client,
                    client_id: res.client.id,
                    patient: res.patient,
                    patient_id: res.patient.id
                });
            } else {
                this._message("Unable to get boarding details");
            }
        },
        openModalCancel() {
            this.isLoadingFooter = true;
            this.$refs.modalCancel.open(this.record.recordId);
        },
        openModalExtend() {
            this.isLoadingFooter = true;
            this.$refs.modalExtend.open(this.record.recordId);
        },
        modalLoaded() {
            this.isLoadingFooter = false;
            this.closeAllModalAndPopover();
        },
        openCheckOut() {
            this.isLoadingFooter = true;
            this.$refs.modalCheckout.open(this.record.recordId);
        },
        async split() {
            document.body.style.cursor = "wait";
            this.isLoadingFooter = true;

            let res = await BoardingService.getBoardingById(
                this.record.recordId
            );

            this.modalLoaded();
            document.body.style.cursor = "";

            if (!res) {
                this._message("Unable to get boarding details");
                return;
            }

            this.$refs.modalReservation.openSplit(res);
        }
    }
};
</script>

<style scoped>
td.rate {
    padding-left: 15px;
}
ul {
    list-style: none;
}
ul {
    padding-left: 0;
}
::v-deep .modal-footer {
    justify-content: flex-start;
    padding-top: 13px !important;
    padding-bottom: 13px !important;
}
::v-deep .reservation-preview .modal-dialog {
    min-width: 550px;
    width: 550px;
}
</style>
